<template>
  <AppBar />
  <v-main style="min-height: 300px">
    <div>
      <h1 class="page-title"># Experience</h1>

      <div class="about-block">
        <!-- Education -->
        <v-card class="mb-6" variant="outlined" color="grey-darken-3">
          <v-card flat color="transparent">
            <v-container>
              <div class="d-flex align-center">
                <v-icon class="mr-3" color="primary" size="32">mdi-school</v-icon>
                <div>
                  <div class="text-h6 font-weight-light">Education</div>
                </div>
              </div>
            </v-container>
          </v-card>

          <v-card-text class="py-0">
            <v-timeline density="compact" align="start">
              <v-timeline-item
                v-for="(education, index) in educations"
                :key="'edu-' + index"
                dot-color="primary"
                :icon="education.icon"
                size="small"
              >
                <div class="d-flex pt-1">
                  <div class="timeline-duration me-4">
                    <strong>{{ education.duration }}</strong>
                  </div>
                  <div>
                    <strong>{{ education.school }}</strong>
                    <div class="text-caption">{{ education.degree }}</div>
                  </div>
                </div>
              </v-timeline-item>
            </v-timeline>
          </v-card-text>
        </v-card>

        <!-- Research Experience -->
        <v-card class="mb-6" variant="outlined" color="grey-darken-3">
          <v-card flat color="transparent">
            <v-container>
              <div class="d-flex align-center">
                <v-icon class="mr-3" color="info" size="32">mdi-flask</v-icon>
                <div>
                  <div class="text-h6 font-weight-light">Research Experience</div>
                </div>
              </div>
            </v-container>
          </v-card>

          <v-card-text class="py-0">
            <v-timeline density="compact" align="start">
              <v-timeline-item
                v-for="(exp, index) in researchExperience"
                :key="'research-' + index"
                dot-color="info"
                :icon="exp.icon"
                size="small"
              >
                <div class="d-flex pt-1">
                  <div class="timeline-duration me-4">
                    <strong>{{ exp.duration }}</strong>
                  </div>
                  <div>
                    <strong>{{ exp.title }}</strong>
                    <div class="text-caption">{{ exp.organization }}</div>
                    <div class="text-caption text-grey">{{ exp.description }}</div>
                  </div>
                </div>
              </v-timeline-item>
            </v-timeline>
          </v-card-text>
        </v-card>

        <!-- Industry Experience -->
        <v-card class="mb-6" variant="outlined" color="grey-darken-3">
          <v-card flat color="transparent">
            <v-container>
              <div class="d-flex align-center">
                <v-icon class="mr-3" color="success" size="32">mdi-briefcase</v-icon>
                <div>
                  <div class="text-h6 font-weight-light">Industry Experience</div>
                </div>
              </div>
            </v-container>
          </v-card>

          <v-card-text class="py-0">
            <v-timeline density="compact" align="start">
              <v-timeline-item
                v-for="(exp, index) in industryExperience"
                :key="'industry-' + index"
                dot-color="success"
                :icon="exp.icon"
                size="small"
              >
                <div class="d-flex pt-1">
                  <div class="timeline-duration me-4">
                    <strong>{{ exp.duration }}</strong>
                  </div>
                  <div>
                    <strong>{{ exp.title }}</strong>
                    <div class="text-caption">{{ exp.organization }}</div>
                    <div class="text-caption text-grey">{{ exp.description }}</div>
                  </div>
                </div>
              </v-timeline-item>
            </v-timeline>
          </v-card-text>
        </v-card>
      </div>
    </div>
  </v-main>
</template>

<script>
import AppBar from "../components/AppBar.vue";

export default {
  components: {
    AppBar,
  },

  data: () => ({
    educations: [
      {
        school: "Carnegie Mellon University",
        degree: "Master of Science in Computer Vision",
        duration: "Aug. 2026 - Dec. 2027",
        icon: "mdi-school",
      },
      {
        school: "National Yang Ming Chiao Tung University",
        degree: "Bachelor of Science in Computer Science",
        duration: "Sep. 2021 - Jun. 2025",
        icon: "mdi-school",
      },
    ],
    researchExperience: [
      {
        title: "Undergraduate Research Assistant",
        organization:
          "Comp Photo Lab, National Yang Ming Chiao Tung University",
        duration: "Jul. 2023 - Present",
        icon: "mdi-flask",
        description:
          "Advised by Prof. Yu-Lun Liu. Conducted research on 3D Reconstruction, Diffusion Models, and Vision-Language Navigation.",
      },
      {
        title: "Research Assistant",
        organization: "Metals & Applications Laboratory",
        duration: "Oct. 2023 - Oct. 2024",
        icon: "mdi-flask",
        description:
          "Advised by Prof. E-Wen Huang. Conducted research on utilizing ML models to predict the parameters of shape memory alloys.",
      },
    ],
    industryExperience: [
      {
        title: "Surveillance Product Developer Intern — Video Deep Learning",
        organization: "Synology",
        duration: "Oct. 2025 - May 2026",
        icon: "mdi-briefcase",
        description:
          "Built and deployed real-time vision-based surveillance systems, owning the end-to-end ML lifecycle. Improved PPE and fall detection by 30% and 10% via data cleaning and feature engineering, and optimized safety-critical inference modules with TensorRT for a 200% throughput boost.",
      },
      {
        title: "3D Multimedia System Development Intern",
        organization: "Industrial Technology Research Institute (ITRI)",
        duration: "Jun. 2025 - Oct. 2025",
        icon: "mdi-briefcase",
        description:
          "Built a high-resolution, real-time telepresence system leveraging 3D Gaussian Splatting, enabling high-fidelity virtual interaction at a stable 30 fps for remote collaboration.",
      },
      {
        title: "AI Engineer Intern",
        organization: "Big Data Mobile (Medical AI Startup)",
        duration: "Jul. 2024 - Sep. 2024",
        icon: "mdi-briefcase",
        description:
          "Implemented and trained a diffusion model for synthetic ECG signal generation to mitigate dataset imbalance, reaching 97% accuracy on a pre-trained ECG classifier.",
      },
    ],
  }),
};
</script>

<style>
.page-title {
  color: lightskyblue;
  font-size: 36px;
  text-align: center;
  margin-top: 50px;
  margin-bottom: 30px;
}

.about-block {
  text-align: left;
  margin-left: 15%;
  margin-right: 15%;
}

.timeline-duration {
  min-width: 160px;
  font-size: 13px;
  /* color: #aaa; */
  color: #00ced1;
}

/* Make timeline text more visible on dark background */
.about-block .v-timeline-item strong {
  color: #fff;
}
.about-block .v-timeline-item .text-caption {
  color: #ccc;
}
.about-block .text-h6 {
  color: lightskyblue;
}


</style>

